/**
 * sroi.js
 * 
 * API service for SROI (Social Return on Investment) data
 */

import axiosInstance from "./axios-instance";

const sroi = {
    /**
     * Get SROI data with optional filtering
     * @param {Object} filters - Filter parameters
     * @param {string} filters.client_id - Optional client ID filter
     * @param {string} filters.user_id - Optional user ID filter
     * @param {string} filters.start_date - Optional start date filter (YYYY-MM-DD)
     * @param {string} filters.end_date - Optional end date filter (YYYY-MM-DD)
     * @returns {Promise} API response
     */
    getSroiData: (filters = {}) => {
        const params = new URLSearchParams();
        
        if (filters.client_id) params.append('client_id', filters.client_id);
        if (filters.user_id) params.append('user_id', filters.user_id);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        
        return axiosInstance.get(`/app/sroi/data?${params.toString()}`);
    },

    /**
     * Get aggregated SROI summary data
     * @param {Object} filters - Filter parameters
     * @param {string} filters.client_id - Optional client ID filter
     * @param {string} filters.user_id - Optional user ID filter
     * @param {string} filters.start_date - Optional start date filter (YYYY-MM-DD)
     * @param {string} filters.end_date - Optional end date filter (YYYY-MM-DD)
     * @returns {Promise} API response
     */
    getSroiSummary: (filters = {}) => {
        const params = new URLSearchParams();
        
        if (filters.client_id) params.append('client_id', filters.client_id);
        if (filters.user_id) params.append('user_id', filters.user_id);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        
        return axiosInstance.get(`/app/sroi/summary?${params.toString()}`);
    },

    /**
     * Get SROI data and trends for a specific client
     * @param {string} clientId - Client ID
     * @param {Object} filters - Filter parameters
     * @param {string} filters.start_date - Optional start date filter (YYYY-MM-DD)
     * @param {string} filters.end_date - Optional end date filter (YYYY-MM-DD)
     * @returns {Promise} API response
     */
    getClientSroiTrends: (clientId, filters = {}) => {
        const params = new URLSearchParams();
        
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        
        return axiosInstance.get(`/app/sroi/client/${clientId}?${params.toString()}`);
    },

    /**
     * Get all SROI mapping values
     * @returns {Promise} API response
     */
    getSroiValues: () => {
        return axiosInstance.get('/app/sroi/values');
    },

    /**
     * Update SROI mapping values
     * @param {Array} values - Array of SROI value objects
     * @param {string} values[].field_name - The field name to update
     * @param {number} values[].dollar_per_star - The dollar value per star rating
     * @returns {Promise} API response
     */
    updateSroiValues: (values) => {
        return axiosInstance.patch('/app/sroi/values', { values });
    }
};

export default sroi;